// Adding customers and sites (WRMM-80): what the API answers, shared with the console.

/** A new customer with its first site. */
export interface AddedCustomer { organization: { id: string; name: string }; site: AddedSite }
export interface AddedSite { id: string; organizationId: string; name: string }

/** A customer user, as a workspace owner sees them (WRMM-134): set up once they have a password and a second factor. */
export interface CustomerUserView {
  id: string; organizationId: string; name: string | null; email: string | null; active: boolean; setUp: boolean;
  /** Whether a setup link was ever made for them (its email sent or tried): an owner can add someone now and send it later (WRMM-220). */
  setupEmailSent: boolean; createdAt: string;
  devices: Array<{ id: string; hostname: string; displayName?: string | null; supportId?: string }>;
  /** Whether the setup email this action made was handed to the mail service (false: it wasn't sent; try Resend). */
  emailSent?: boolean;
}
/** A device in a customer user's portal: basic information only (WRMM-59). */
export interface PortalDevice { id: string; hostname: string; displayName?: string | null; supportId?: string; os: string | null; status: string | null; lastSeenAt: string | null }

/** A grant of staff access (WRMM-133): every customer (`organizationId` null) or one, with capabilities. */
export type StaffCapability = 'devices.enroll' | 'diagnostics.run' | 'scripts.run' | 'jobs.approve' | 'remote.connect' | 'end_users.manage';
/** `siteId`: a site scope a member already has (kept when their access is edited; invitations never carry one). */
export interface StaffGrant { organizationId: string | null; siteId?: string | null; capabilities: StaffCapability[] }
/** A member of the workspace, as people managers see them on the People page. */
export interface StaffMemberView {
  personId: string; name: string | null; email: string | null; role: 'owner' | 'admin' | 'technician'; you: boolean;
  grants: Array<StaffGrant & { organizationName: string | null; siteId: string | null; siteName: string | null }>;
  /** Their second factors (never what they are): keys and whether an authenticator app is set up. */
  keys: number; authenticator: boolean;
}
export interface StaffInvitationView {
  id: string; email: string; admin: boolean; grants: StaffGrant[]; createdAt: string; expiresAt: string; invitedBy: string;
  emailSent?: boolean;
}
export interface PeopleView { emailSent?: boolean; members: StaffMemberView[]; invitations: StaffInvitationView[]; allowlist: string[]; you: { owner: boolean; capabilities: StaffCapability[] } }

// Deleting an empty customer (WRMM-336, slice 1): what the API answers, shared with the console.

/** A public code (main.ts SafeErrors): the customer still holds something, listed by kind in `remaining`. */
export const CUSTOMER_NOT_EMPTY_CODE = 'customer_not_empty';
/**
 * What a customer can still hold that deleting it would lose, by kind (never its data). Any of them refuses deleting it
 * outright; the customer is archived instead (WRMM-337, archive.ts).
 */
export const REMAINING_KINDS = ['devices', 'used_installers', 'staff_access', 'staff_invitations', 'end_users', 'service_desk', 'policies',
  'customer_migration', 'crowdstrike', 'falcon_alerts', 'remote_group', 'remote_pilot', 'agent_elevations', 'other'] as const;
export type RemainingKind = typeof REMAINING_KINDS[number];
export const isRemainingKind = (value: unknown): value is RemainingKind => (REMAINING_KINDS as readonly unknown[]).includes(value);
/** What deleting the customer would delete, as its key touch approves it: the customer, its sites, its unused installer links and its CrowdStrike box (off). */
export interface CustomerDeletionPreview {
  id: string; name: string; sites: Array<{ id: string; name: string }>;
  /** Installer links for its sites that never added a device: deleted with it, so they can't add one afterwards. */
  installerLinks: number;
  /** Its CrowdStrike box, recorded off: a setting, deleted with it. */
  crowdstrikeBox: boolean;
}
export interface CustomerDeletionOptions { customer: CustomerDeletionPreview; options: Record<string, unknown> }
export interface DeletedCustomer { deleted: true; id: string; name: string }

// Archiving a customer that has or had devices (WRMM-337): what the API answers, shared with the console.

/** A public code (main.ts SafeErrors): the customer is empty, so it is deleted outright (WRMM-336), never archived. */
export const CUSTOMER_EMPTY_CODE = 'customer_empty';
/** Removing a device leaves its CrowdStrike sensor (Harry asked, 2026-10-29): said wherever an archive's Falcon is. */
export const FALCON_SENSORS_STAY = 'CrowdStrike sensors stay installed on these computers and keep billing until removed in CrowdStrike (WRMM-317 will automate it).';
/** A public code (main.ts SafeErrors): the name typed to confirm an archive isn't the customer's (Harry, 2026-10-29: "a small guard"). */
export const CONFIRM_NAME_MISMATCH_CODE = 'confirm_name_mismatch';
/** A public code (main.ts SafeErrors): something under way stops the archive, listed by kind in `blocking`. */
export const CUSTOMER_ARCHIVE_BLOCKED_CODE = 'customer_archive_blocked';
/**
 * What is under way that archiving can't stop safely, by kind: a customer migration running, a Falcon child being created,
 * install/removal monitoring or a protection alert pending, a WITTCO Remote pilot customer, an agent's temporary access to it.
 */
export const ARCHIVE_BLOCKERS = ['migration_running', 'crowdstrike_requested', 'falcon_install_pending', 'sentinelone_removal_pending', 'remote_pilot', 'agent_elevations'] as const;
export type ArchiveBlocker = typeof ARCHIVE_BLOCKERS[number];
export const isArchiveBlocker = (value: unknown): value is ArchiveBlocker => (ARCHIVE_BLOCKERS as readonly unknown[]).includes(value);
/** What archiving the customer does, as its key touch approves it. */
export interface CustomerArchivePreview {
  id: string; name: string; sites: Array<{ id: string; name: string }>;
  /** Its devices, removed now as a device removal does (WITTCO Remote uninstalled, jobs canceled, credentials revoked). */
  devices: Array<{ id: string; hostname: string; displayName?: string | null; supportId?: string }>;
  /** Installer links revoked, so they add no device. */
  installerLinks: number;
  /** End users turned off, so they can't sign in. */
  endUsers: number;
  /** Its Falcon child CID: it stays at CrowdStrike until a deletion request there removes it (the child itself isn't billed). */
  falconCid: string | null;
  /**
   * Whether CrowdStrike sensors may be on its computers (it has a Falcon child, or one reported Falcon): removing a device
   * leaves the sensor installed and billed until removed in CrowdStrike (WRMM-317 will automate it).
   */
  falconSensors: boolean;
  /** The day (UTC) its records are purged, a year from now. */
  purgeOn: string;
}
export interface CustomerArchiveOptions { customer: CustomerArchivePreview; options: Record<string, unknown> }
export interface ArchivedCustomerResult {
  /** Null when the answer was lost after the archive committed (it is read again then). */
  archived: true; id: string; name: string; devicesRemoved: number | null; purgeAfter: string; falconCid: string | null;
  /** Whether the workspace's alert email was sent about its Falcon child (null when it has none). */
  falconAlertSent: boolean | null;
}
/** An archived customer, for its MSP-wide owners: read-only until purged. */
export interface ArchivedCustomerView { id: string; name: string; archivedAt: string; purgeAfter: string; falconCid: string | null }
